/**
 * `/welcome` (#648): the last two steps of signing up, after the consent gate's first save (RegKey, RegCourses).
 *
 * Moodle first, in two steps (RegMoodle, RegKey): what DaVinci reads and never does, then the paste-a-key field. Once
 * Moodle is connected (just now, or before) the step is Courses: one row per course with the Shown tick and the colour. Moodle single sign-on is not
 * built, so the key is the only way in, and "Skip for now" goes Home so a learner without a key is not stuck.
 *
 * Drawn in `AuthStage` but inside the app shell, so it has a session and has passed the consent gate: no key is
 * handled before consent. Nothing here is a status; course colours are labels (never a verdict).
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { AuthStage, SignupSteps } from '../../app/auth-layout.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { useLocale, useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';
import { ConnectorActions, connectorMessages, MismatchNotice, useMoodleConnector } from '../settings/moodle-connector.js';
import { ExternalLinkIcon, moodleMessages, useMoodleConnect } from '../settings/moodle-section.js';
import { WelcomeCourses } from './welcome-courses.js';

const messages = defineMessages({
  en: {
    loading: 'One moment…',
    h1: 'Bring your courses',
    readOnly: 'read-only',
    readsH: 'Reads',
    r1: 'Sheets and recitations',
    r2: 'Due dates',
    r3: 'Your name',
    neverH: 'Never',
    n1: 'Submits or deletes',
    n2: 'Grades or messages',
    n3: 'Your password',
    skip: 'Skip for now',
    keyHint: 'Checked before it is saved. Never shown again.',
    next: 'Next: paste your key',
    keyH1: 'Paste your Moodle key',
    copyThis: 'Copy this one, then come back',
    back: 'Back',
  },
  he: {
    loading: 'רק רגע…',
    h1: 'חיבור הקורסים',
    readOnly: 'קריאה בלבד',
    readsH: 'קורא',
    r1: 'תרגילים ותרגולים',
    r2: 'תאריכי הגשה',
    r3: 'השם שלך',
    neverH: 'אף פעם',
    n1: 'לא מגיש ולא מוחק',
    n2: 'לא ציונים ולא הודעות',
    n3: 'לא הסיסמה שלך',
    skip: 'דילוג בינתיים',
    keyHint: 'נבדק לפני שהוא נשמר. לא יוצג שוב.',
    next: 'הבא: הדבקת מפתח',
    keyH1: 'הדבקת מפתח מודל',
    copyThis: 'מעתיקים את זה וחוזרים לכאן',
    back: 'חזרה',
  },
});

export function WelcomePage(): React.JSX.Element {
  const t = useMessages(messages);
  const { connection, connected } = useMoodleConnect();

  if (connection.isError) {
    return (
      <AuthStage glyph="🎓">
        <SignupSteps current="moodle" />
        <h1>{t.h1}</h1>
        <FailureNotice error={connection.error} onRetry={() => void connection.refetch()} />
      </AuthStage>
    );
  }
  if (!connection.isSuccess) {
    return (
      <AuthStage glyph="🎓">
        <SignupSteps current="moodle" />
        <h1>{t.h1}</h1>
        <p role="status">{t.loading}</p>
      </AuthStage>
    );
  }
  return connected ? <WelcomeCourses /> : <WelcomeMoodle />;
}

function WelcomeMoodle(): React.JSX.Element {
  const t = useMessages(messages);
  const m = useMessages(moodleMessages);
  const { locale } = useLocale();
  // Which of the two steps shows. Local to the page, so Back keeps what was typed (the form stays in this component).
  const [step, setStep] = useState<'bring' | 'key'>('bring');
  const [token, setToken] = useState('');
  const { connect, sentence, securityKeysUrl, mismatch } = useMoodleConnect();
  const connector = useMoodleConnector();
  const c = useMessages(connectorMessages);
  const heading = useRef<HTMLHeadingElement>(null);
  const shown = useRef(step);

  // A step change moves focus to the new heading; the first render, and a re-run of the effect, leave it alone.
  useEffect(() => {
    if (shown.current === step) return;
    shown.current = step;
    heading.current?.focus();
  }, [step]);

  function submit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const value = token.trim();
    if (value === '') return;
    setToken('');
    connect.mutate(value);
  }

  if (step === 'bring') {
    return (
      <AuthStage glyph="🎓">
        <SignupSteps current="moodle" />
        <h1 ref={heading} tabIndex={-1}>
          {t.h1}
        </h1>

        <div className="welcome-link" aria-hidden="true">
          <span className="welcome-link-end">
            <span className="welcome-link-tile welcome-link-tile-d">D</span>DaVinci
          </span>
          <span className="welcome-link-arrow">
            <span>{locale === 'he' ? '→' : '←'}</span>
            {t.readOnly}
          </span>
          <span className="welcome-link-end">
            <span className="welcome-link-tile">🎓</span>Moodle
          </span>
        </div>

        <div className="welcome-panels">
          <div className="welcome-panel welcome-panel-reads">
            <p>{t.readsH}</p>
            <ul>
              {[t.r1, t.r2, t.r3].map((line) => (
                <li key={line}>
                  <span aria-hidden="true">✓</span>
                  {line}
                </li>
              ))}
            </ul>
          </div>
          <div className="welcome-panel">
            <p>{t.neverH}</p>
            <ul>
              {[t.n1, t.n2, t.n3].map((line) => (
                <li key={line}>
                  <span aria-hidden="true">✕</span>
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {connector.mode === 'none' ? (
          <div className="welcome-actions">
            {mismatch ? <MismatchNotice /> : null}
            {connector.showNoneLine ? <p className="connector-line">{c.noneLine}</p> : null}
            <button type="button" className="auth-submit" onClick={() => setStep('key')}>
              {t.next}
            </button>
            <Link className="welcome-skip" to="/">
              {t.skip}
            </Link>
          </div>
        ) : (
          <div className="welcome-actions">
            <ConnectorActions connector={connector} variant="welcome" onUseKey={() => setStep('key')} mismatch={mismatch} />
            <Link className="welcome-skip" to="/">
              {t.skip}
            </Link>
          </div>
        )}
      </AuthStage>
    );
  }

  return (
    <AuthStage glyph="🔑">
      <SignupSteps current="moodle" />
      <h1 ref={heading} tabIndex={-1}>
        {t.keyH1}
      </h1>

      {securityKeysUrl !== null ? (
        <a className="welcome-keys-link" href={securityKeysUrl} target="_blank" rel="noopener noreferrer">
          <span>{m.openKeys}</span>
          <ExternalLinkIcon />
        </a>
      ) : (
        <p className="welcome-steps">{m.step1}</p>
      )}

      {/* A picture of the row to look for on Moodle's page, not a key DaVinci holds: the dots are drawn, no value is
          behind them, and the copy icon is decoration with no control. */}
      <div className="welcome-key-card">
        <p>{t.copyThis}</p>
        <div className="welcome-key-sample" dir="ltr">
          <span>
            <strong>Moodle mobile web service</strong>
            <span aria-hidden="true">••••••••••••••••</span>
          </span>
          <span className="welcome-key-copy" aria-hidden="true">
            ⧉
          </span>
        </div>
      </div>

      {sentence !== undefined ? (
        <p className="notice notice-error" role="alert">
          {sentence}
        </p>
      ) : mismatch ? (
        <MismatchNotice />
      ) : connect.isError ? (
        <FailureNotice error={connect.error} />
      ) : null}

      <form className="auth-form welcome-form" onSubmit={submit}>
        <div className="field">
          <label htmlFor="welcome-moodle-key">{m.tokenLabel}</label>
          <input
            id="welcome-moodle-key"
            name="moodleKey"
            type="password"
            dir="ltr"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            aria-describedby="welcome-moodle-key-hint"
            value={token}
            onChange={(event) => setToken(event.target.value)}
          />
          <span id="welcome-moodle-key-hint" className="field-hint">
            <span aria-hidden="true">🔒 </span>
            {t.keyHint}
          </span>
        </div>
        <div className="welcome-actions">
          <button type="submit" className="auth-submit" disabled={connect.isPending}>
            {connect.isPending ? m.connecting : m.connect}
          </button>
          <button type="button" className="welcome-back" onClick={() => setStep('bring')}>
            {t.back}
          </button>
          <Link className="welcome-skip" to="/">
            {t.skip}
          </Link>
        </div>
      </form>
    </AuthStage>
  );
}
